import { useCallback, useMemo } from "react";
import { CircleMarker, Tooltip } from "react-leaflet";
import L from "leaflet";
import type { Map as LeafletMap } from "leaflet";
import FloraIQMap from "@/components/shared/FloraIQMap";
import { NoRealData } from "@/components/shared";
import type { TreeApi } from "@/lib/api";
import { RISK_CLASS_ORDER } from "@/config/projetos-config";

interface Props {
  arvores: TreeApi[];
}

const SEM_AVALIACAO = "#9AA0A6";

/** Exemplares do projeto com coordenada, coloridos pela ultima classe de risco. */
export default function ProjectMapPreview({ arvores }: Props) {
  const comCoordenada = useMemo(
    () => arvores.filter((a) => a.latitude != null && a.longitude != null),
    [arvores]
  );

  const enquadrar = useCallback(
    (map: LeafletMap) => {
      if (comCoordenada.length === 0) return;
      const bounds = L.latLngBounds(comCoordenada.map((a) => [a.latitude as number, a.longitude as number]));
      map.fitBounds(bounds, { padding: [30, 30], maxZoom: 18 });
    },
    [comCoordenada]
  );

  if (comCoordenada.length === 0) {
    return (
      <NoRealData
        icon="ri-map-pin-line"
        title="Nenhum exemplar georreferenciado"
        description="O mapa do projeto mostra os exemplares arbóreos com coordenada. Este projeto ainda não tem nenhum."
      />
    );
  }

  return (
    <div className="space-y-3">
      <div className="relative rounded-lg overflow-hidden border border-background-200" style={{ height: 380 }}>
        <FloraIQMap style={{ height: 380 }} onMapReady={enquadrar}>
          {comCoordenada.map((a) => {
            const cor = RISK_CLASS_ORDER.find((r) => r.key === a.latest_risk_class)?.color ?? SEM_AVALIACAO;
            return (
              <CircleMarker
                key={a.id}
                center={[a.latitude as number, a.longitude as number]}
                radius={6}
                pathOptions={{ color: "#ffffff", weight: 1.5, fillColor: cor, fillOpacity: 0.9 }}
              >
                <Tooltip>{a.cta_code ?? `Exemplar ${a.id}`}</Tooltip>
              </CircleMarker>
            );
          })}
        </FloraIQMap>
      </div>
      <div className="flex items-center gap-3 flex-wrap text-xs text-foreground-600">
        {RISK_CLASS_ORDER.map((r) => (
          <span key={r.key} className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full" style={{ background: r.color }}></span>
            Risco {r.label.toLowerCase()}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className="w-3 h-3 rounded-full" style={{ background: SEM_AVALIACAO }}></span>
          Sem avaliação de risco
        </span>
        <span className="text-foreground-400">
          {comCoordenada.length} de {arvores.length} exemplares com coordenada
        </span>
      </div>
    </div>
  );
}
